import classNames from 'classnames';
import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { JobStatus } from '~/generated/graphql';
import { Button } from '../form/SubmitButton';
import { Dialog } from '../ui/Dialog';
import { Switch } from '../ui/nucleus/Switch';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    status: JobStatus,
    options: { notify: boolean; comment: string | undefined }
  ) => void;
  jobName: string;
  assigneeName: string;
  action: 'offer' | 'confirm';
  originalDate?: Date;
  targetDate?: Date;
  isLoading?: boolean;
};

export function RescheduleJobDialog({
  isOpen,
  onClose,
  onSubmit,
  jobName,
  assigneeName,
  action,
  originalDate,
  targetDate,
  isLoading = false,
}: Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [notify, setNotify] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset submitting state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Reset internal submitting state when external loading state changes
  useEffect(() => {
    if (!isLoading) {
      setIsSubmitting(false);
    }
  }, [isLoading]);

  const submitButtonLabel =
    originalDate && targetDate ? action + ' move' : action + ' job';

  // Create move message if dates are available
  const getMessage = () => {
    if (originalDate && targetDate) {
      const fromDate = format(originalDate, 'MMM d, yyyy');
      const toDate = format(targetDate, 'MMM d, yyyy');
      return (
        <>
          You are about to move the job{' '}
          <span className='font-semibold'>{jobName}</span> from{' '}
          <span className='font-semibold'>{fromDate}</span> to{' '}
          <span className='font-semibold'>{toDate}</span>{' '}
          {action === 'offer' ? 'for' : 'and notify'}{' '}
          <span className='font-semibold'>{assigneeName}</span>
        </>
      );
    }

    // Fallback to original message
    return (
      <>
        You are about to {action} the job{' '}
        <span className='font-semibold'>{jobName}</span>{' '}
        {action === 'offer' ? 'to' : 'for'}{' '}
        <span className='font-semibold'>{assigneeName}</span>
      </>
    );
  };

  return (
    <Dialog title='Almost there!' show={isOpen} onClose={onClose}>
      <div className='flex flex-col flex-wrap gap-3 px-5'>
        <p className='px-2 pt-3'>{getMessage()}</p>
        <Switch
          label='Notify assignees'
          checked={notify}
          onChange={setNotify}
        />
        <textarea
          ref={inputRef}
          className={classNames(
            'input mt-1 w-full resize-none rounded-md border border-grey-20 px-3 py-2.5 focus-visible:border-brand focus-visible:outline-none',
            !notify && 'hidden'
          )}
          placeholder='Add optional message...'
          autoFocus
        ></textarea>
        <div className='flex gap-3 self-end pb-5 pt-1'>
          <button
            className={classNames(
              'px-4 py-2 text-sm font-medium uppercase text-brand-dark transition-all duration-500 hover:text-brand'
            )}
            onClick={onClose}
          >
            Go back
          </button>
          <Button
            data-usetiful={`${action}`}
            type='button'
            size='small'
            disabled={isSubmitting || isLoading}
            onClick={() => {
              if (isSubmitting || isLoading) return;
              setIsSubmitting(true);

              if (action === 'confirm') {
                onSubmit(JobStatus.Accepted, {
                  notify,
                  comment: inputRef.current?.value,
                });
                return;
              }

              onSubmit(JobStatus.Offered, {
                notify,
                comment: inputRef.current?.value,
              });
            }}
          >
            {isSubmitting || isLoading ? 'Processing...' : submitButtonLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
